import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { AttorneySelect } from '@/components/matter/AttorneySelect';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Switch, SwitchField } from '@/components/ui/catalyst/switch';
import { Label } from '@/components/ui/catalyst/fieldset';
import { cn } from '@/lib/utils';
import type { ReadSigningConfig } from '@/services/api/generated/templatesV2Service';
import {
  useTemplateSigningConfigDeleteMutation,
  useTemplateSigningConfigUpdateMutation,
} from '@/services/api/templatesV2ServiceEnhanced';

interface TemplateSigningPanelProps {
  templateId: string;
  signingConfig: ReadSigningConfig;
  firmId?: string | null;
}

export const TemplateSigningPanel = ({
  templateId,
  signingConfig,
  firmId,
}: TemplateSigningPanelProps) => {
  const [open, setOpen] = useState(false);

  const [updateConfig] = useTemplateSigningConfigUpdateMutation();
  const [deleteConfig, { isLoading: isDeleting }] =
    useTemplateSigningConfigDeleteMutation();

  const handleToggle = useCallback(
    async (field: 'client_signature_required' | 'attorney_signature_required', value: boolean) => {
      try {
        await updateConfig({
          templateId,
          body: { [field]: value },
        }).unwrap();
      } catch {
        toast.error('Failed to update signing config');
      }
    },
    [templateId, updateConfig],
  );

  const handleAttorneyChange = useCallback(
    async (attorneyId: string | null) => {
      try {
        await updateConfig({
          templateId,
          body: { default_attorney_id: attorneyId },
        }).unwrap();
      } catch {
        toast.error('Failed to update default attorney');
      }
    },
    [templateId, updateConfig],
  );

  const handleDelete = useCallback(async () => {
    try {
      await deleteConfig({ templateId }).unwrap();
      toast.success('Signing configuration removed');
    } catch {
      toast.error('Failed to remove signing config');
    }
  }, [templateId, deleteConfig]);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            E-Signatures
          </span>
          <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
            {signingConfig.provider === 'ADOBE_SIGN' ? 'Adobe Sign' : signingConfig.provider}
          </span>
        </div>
        <ChevronDownIcon
          className={cn(
            'h-4 w-4 text-zinc-400 transition-transform',
            open && 'rotate-180',
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-3 pb-2">
          {/* Client signature */}
          <SwitchField>
            <Label className="text-sm text-zinc-700 dark:text-zinc-300">
              Client signature required
            </Label>
            <Switch
              color="indigo-steel"
              checked={signingConfig.client_signature_required}
              onChange={(val) => handleToggle('client_signature_required', val)}
            />
          </SwitchField>

          {/* Attorney signature */}
          <SwitchField>
            <Label className="text-sm text-zinc-700 dark:text-zinc-300">
              Attorney signature required
            </Label>
            <Switch
              color="indigo-steel"
              checked={signingConfig.attorney_signature_required}
              onChange={(val) => handleToggle('attorney_signature_required', val)}
            />
          </SwitchField>

          {/* Default attorney (when attorney signature is required) */}
          {signingConfig.attorney_signature_required && firmId && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-zinc-500">Default Attorney</span>
              <AttorneySelect
                firmId={firmId}
                value={signingConfig.default_attorney_id ?? null}
                onChange={handleAttorneyChange}
                placeholder="Select default attorney"
              />
            </div>
          )}

          {/* Remove signing config */}
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
          >
            {isDeleting ? 'Removing...' : 'Remove signing configuration'}
          </button>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
